import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	audience_email_AboveList,
	forBusiness_email_audience,
	forBusiness_email_choose,
	forBusiness_email_features,
	forBusiness_email_generate,
	forBusiness_email_matters,
	forBusiness_email_perfect,
	matters_email_AboveList,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import audience from '@/public/images/for-business/email/audience.webp';
import choose from '@/public/images/for-business/email/choose.webp';
import features from '@/public/images/for-business/email/features.webp';
import generate from '@/public/images/for-business/email/generate.webp';
import imgHero from '@/public/images/for-business/email/hero.webp';
import matter from '@/public/images/for-business/email/matter.webp';
import perfect from '@/public/images/for-business/email/perfect.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './EmailMarketingLanding.module.scss';

interface Props {}
const EmailMarketingLanding: React.FC<Props> = () => {
	const { t } = useTranslationWithLocale('email-marketing-visuals', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.EMAIL_MARKETING_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.EMAIL_MARKETING_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.EMAIL_MARKETING_LANDING,
	});
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['email__hero-title'],
					mainSubtitle: styles['email__hero-subtitle'],
					picture: styles['email__hero-picture'],
				}}
			/>

			{/* challenges */}
			<ImageTextSection
				title={t('matterData_mainTitle')}
				subtitle1={t('matterData_mainSubtitle1')}
				specialListItem
				data={forBusiness_email_matters}
				textsAboveList={matters_email_AboveList}
				image={matter}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* audience */}
			<ImageTextSection
				title={t('audienceData_mainTitle')}
				subtitle1={t('audienceData_mainSubtitle1')}
				data={forBusiness_email_audience}
				textsAboveList={audience_email_AboveList}
				image={audience}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['email__audience-image-container'],
					list: styles['email__audience-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* choose */}
			<ImageTextSection
				title={t('chooseData_mainTitle')}
				data={forBusiness_email_choose}
				image={choose}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* generate */}
			<ImageTextSection
				title={t('generateData_mainTitle')}
				data={forBusiness_email_generate}
				image={generate}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['email__generate-image-container'],
					list: styles['email__generate-list'],
					section: styles['email__generate-section'],
				}}
			/>

			{/* features */}
			<ImageTextSection
				title={t('featuresData_mainTitle')}
				data={forBusiness_email_features}
				image={features}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['email__features-section'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				data={forBusiness_email_perfect}
				image={perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['email__perfect-image-container'],
					list: styles['email__perfect-list'],
				}}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title2={t('tryLiftData_title2')}
				title_green={t('tryLiftData_greenText')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['email__tryLift-title'],
					subtitleClass: styles['email__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default EmailMarketingLanding;
